import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import {
    fetchNav2NavigationBehavior,
    fetchNav2NavigationBehaviorLiveStage,
    type Nav2BehaviorAction,
    type Nav2BehaviorActionType,
    type Nav2BehaviorStage,
    type Nav2BehaviorTreeSettings,
} from "lib/nav2ParamsApi";
import { useARC } from "providers/ARC/ARCProvider";
import { useNavigateToPose } from "providers/navigation/NavigateToPoseProvider";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { getNav2ParamsApiBaseUrl } from "lib/nav2ParamsApi";

const POLL_MS = 1500;

const STAGE_ORDER: Array<keyof Nav2BehaviorTreeSettings & ("planning" | "controller" | "recovery")> = [
    "planning",
    "controller",
    "recovery",
];

const STAGE_META: Record<
    string,
    { labelKey: string; border: string; badge: string }
> = {
    planning: { labelKey: "planning", border: "border-l-cyan-400", badge: "bg-cyan-400" },
    controller: { labelKey: "controller", border: "border-l-amber-400", badge: "bg-amber-400" },
    recovery: { labelKey: "recovery", border: "border-l-rose-400", badge: "bg-rose-400" },
};

const ACTION_TYPES_BY_STAGE: Record<string, Nav2BehaviorActionType[]> = {
    planning: [
        "clear_global_costmap",
        "wait",
        "retry_planner",
        "alternate_planner",
    ],
    controller: [
        "clear_local_costmap",
        "set_collision_monitor",
        "wait",
        "retry_controller",
        "recovery_controller",
    ],
    recovery: ["clear_local_costmap", "clear_global_costmap", "wait", "backup"],
};

/**
 * Map the live stage key published by nav2_bt_navigator to the most specific
 * configured action type in the current stage.
 */
const liveStageToActionType = (stage: string | null): Nav2BehaviorActionType | null => {
    switch (stage) {
        case "recovery_backup":
            return "backup";
        case "recovery_wait":
            return "wait";
        case "recovery_clearing":
            return "clear_local_costmap";
        case "following_recovery":
            return "recovery_controller";
        case "following_retry_wait":
        case "planning_retry_wait":
            return "wait";
        case "planning_navfn":
            return "alternate_planner";
        case "planning_clear_global":
            return "clear_global_costmap";
        case "following":
            return "retry_controller";
        case "planning":
            return "retry_planner";
        default:
            return null;
    }
};

const liveStageToStageName = (
    stage: string | null
): keyof Nav2BehaviorTreeSettings & ("planning" | "controller" | "recovery") | null => {
    if (!stage) return null;
    if (stage.startsWith("planning")) return "planning";
    if (stage.startsWith("following")) return "controller";
    if (stage.startsWith("recovery")) return "recovery";
    if (["navigating"].includes(stage)) return "planning";
    return null;
};

type BranchState =
    | { status: "loading" }
    | { status: "error"; message: string }
    | {
          status: "ready";
          settings: Nav2BehaviorTreeSettings;
          liveStage: string | null;
      };

const DevNavRecoveryPanel: React.FC = () => {
    const { t } = useTranslation(["control", "settings"]);
    const { selectedARC } = useARC();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const { navigationStage, showNavigateToPoseBanner } = useNavigateToPose();

    const baseUrl = useMemo(
        () => getNav2ParamsApiBaseUrl(selectedARC?.host ?? null, isSecure, arcId),
        [selectedARC?.host, isSecure, arcId]
    );

    const [branchState, setBranchState] = useState<BranchState>({ status: "loading" });
    const [lastPolledAt, setLastPolledAt] = useState<number | null>(null);

    const refresh = useCallback(async () => {
        if (!baseUrl) {
            setBranchState({ status: "error", message: t("control:devNav.noBaseUrl") });
            return;
        }
        try {
            const [settingsRes, liveRes] = await Promise.all([
                fetchNav2NavigationBehavior(baseUrl),
                fetchNav2NavigationBehaviorLiveStage(baseUrl),
            ]);
            setBranchState({
                status: "ready",
                settings: settingsRes.settings,
                liveStage: liveRes.current_stage,
            });
            setLastPolledAt(Date.now());
        } catch (err) {
            setBranchState({
                status: "error",
                message: err instanceof Error ? err.message : String(err),
            });
        }
    }, [baseUrl, t]);

    useEffect(() => {
        setBranchState({ status: "loading" });
        void refresh();
    }, [refresh]);

    useEffect(() => {
        if (!baseUrl) return;
        const id = window.setInterval(() => void refresh(), POLL_MS);
        return () => window.clearInterval(id);
    }, [baseUrl, refresh]);

    const activeStage = liveStageToStageName(navigationStage);
    const activeActionType = liveStageToActionType(navigationStage);
    const navActive = showNavigateToPoseBanner || navigationStage !== "";

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto bg-black100 p-3">
            <div>
                <p className="text-h5 font-semibold text-white">
                    {t("control:devNav.recoveryBranchTitle")}
                </p>
                <p className="mt-0.5 text-[11px] text-black40">
                    {t("control:devNav.recoveryBranchDescription")}
                </p>
            </div>

            {!navActive ? (
                <div className="rounded-r5 border border-black70 bg-black90 p-3">
                    <p className="text-h5 text-black40">
                        {t("control:devNav.noActiveNavigation")}
                    </p>
                </div>
            ) : null}

            {branchState.status === "error" ? (
                <div className="rounded-r5 border border-red-500/40 bg-red-500/10 p-3">
                    <p className="text-h5 text-red-300">{branchState.message}</p>
                    <button
                        type="button"
                        onClick={() => void refresh()}
                        className="mt-2 rounded-r5 border border-black60 bg-black90 px-3 py-1 text-h5 text-white hover:bg-black80"
                    >
                        {t("common:refresh")}
                    </button>
                </div>
            ) : null}

            {branchState.status === "ready" ? (
                <div className="flex flex-col gap-4">
                    {STAGE_ORDER.map((stageName) => (
                        <StagePipeline
                            key={stageName}
                            name={stageName}
                            stage={branchState.settings[stageName]}
                            isActive={activeStage === stageName}
                            activeActionType={activeActionType}
                        />
                    ))}
                </div>
            ) : null}

            {lastPolledAt ? (
                <p className="text-[11px] text-black50">
                    {t("control:devNav.lastPolled", {
                        time: new Date(lastPolledAt).toLocaleTimeString(),
                    })}
                </p>
            ) : null}
        </div>
    );
};

export default DevNavRecoveryPanel;

type StagePipelineProps = {
    name: "planning" | "controller" | "recovery";
    stage: Nav2BehaviorStage;
    isActive: boolean;
    activeActionType: Nav2BehaviorActionType | null;
};

const StagePipeline: React.FC<StagePipelineProps> = ({
    name,
    stage,
    isActive,
    activeActionType,
}) => {
    const { t } = useTranslation(["settings", "control"]);
    const meta = STAGE_META[name];

    const actionIndexByType = useMemo(() => {
        const map = new Map<Nav2BehaviorActionType, number>();
        stage.actions.forEach((action, index) => map.set(action.type, index));
        return map;
    }, [stage.actions]);

    const activeAction = isActive
        ? stage.actions.find((a) => a.type === activeActionType) ?? null
        : null;

    return (
        <section
            className={`rounded-r5 border-l-4 ${meta.border} border-y border-r border-black70 bg-black90 p-3`}
        >
            <div className="flex items-center gap-2">
                <span className={`h-2.5 w-2.5 rounded-full ${meta.badge}`} aria-hidden />
                <h3 className="text-h5 font-semibold text-white">
                    {t(`settings:navigation.behaviorTree.tabs.${name}`, {
                        defaultValue: t(`settings:navigation.behaviorTree.stages.${name}.title`),
                    })}
                </h3>
                {!stage.enabled ? (
                    <span className="ml-auto rounded-full border border-black60 px-2 py-0.5 text-[11px] text-black40">
                        {t("control:devNav.stageDisabled")}
                    </span>
                ) : null}
            </div>

            {name !== "recovery" ? (
                <p className="mt-1 text-[11px] text-black50">
                    {t("control:devNav.primary", {
                        primary: stage.primary_id ?? "—",
                    })}
                </p>
            ) : null}

            {stage.enabled && stage.actions.length > 0 ? (
                <ol className="mt-3 flex flex-col gap-2">
                    {stage.actions.map((action, index) => {
                        const isActiveAction =
                            isActive && action.type === activeActionType;
                        return (
                            <ActionRow
                                key={action.id}
                                index={index}
                                action={action}
                                isActive={isActiveAction}
                            />
                        );
                    })}
                </ol>
            ) : null}

            {activeAction && !stage.actions.find((a) => a.type === activeActionType) ? (
                <p className="mt-2 text-h5 text-amber-300">
                    {t("control:devNav.unconfiguredAction", {
                        stage: t(`settings:navigation.behaviorTree.tabs.${name}`),
                    })}
                </p>
            ) : null}
        </section>
    );
};

type ActionRowProps = {
    index: number;
    action: Nav2BehaviorAction;
    isActive: boolean;
};

const ActionRow: React.FC<ActionRowProps> = ({ index, action, isActive }) => {
    const { t } = useTranslation("settings");
    return (
        <li
            className={`flex items-center gap-2 rounded-r5 border p-2 text-h5 ${
                isActive
                    ? "border-primary2 bg-primary2/20 text-white"
                    : "border-black70 bg-black80 text-black30"
            }`}
        >
            <span
                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                    isActive ? "bg-primary2 text-white" : "bg-black70 text-black40"
                }`}
            >
                {index + 1}
            </span>
            <span className={isActive ? "font-semibold" : undefined}>
                {t(`navigation.behaviorTree.actions.${action.type}`)}
            </span>
            {isActive ? (
                <span className="ml-auto rounded-r3 bg-primary2 px-1.5 py-0.5 text-[10px] text-white">
                    active
                </span>
            ) : null}
        </li>
    );
};
